/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useEffect, useState } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { Button, OpenInNewIcon, StatusBadge, TextOverflow } from "@forge/common";

//@Components
import CustomizedTable from "../customizedtable";
import Toast from "../../../reusablecomponents/toast";
import { ContextualMenuComponent } from "../../../reusablecomponents/contextualmenu";

// @Models
import AddDevicePortModal from "../Modal/AddDevicePortModal";
import ConnectedUsersModal from "../Modal/ConnectedUsersModal";
import ConnectConfirmModal from "../Modal/ConnectConfirmModal";
import { LinkedDeviceListProps } from "../../../models/remoteConnect";
import { LinkedDeviceListHeaderProps } from "../../../models/linkedDeviceList";

//@Services
import { useAddPortDeviceMutation, useApproveOrBlockDeviceMutation, useGetLinkedDevicesListQuery, useRemovePortDeviceMutation } from "../../../services/remote-connect-api";

//@Utils
import { linkedDevicesConnected } from "../../../utils/staticdata";
import { gatewayErrorStatusMessages, handleApiError } from "../../../utils/utility";
import { GatewayStatus, LOCALE_CONST } from "../../../utils/constants";

//@Styles
import './styles.scss';

const ConnectLinkedDevices = () => {
	const { t } = useTranslation();

	const convertFn = (val: string) => t(val);
	const getDeviceID = () => sessionStorage.getItem("linkedDevicesSystemGuid") || "";

	// Fetch linked devices list using the custom hook
	const { data: getLinkedListDetails,
		isLoading: isLinkedListLoading,
		isError: isLinkedListError,
		error: linkedListError,
		isFetching: isLinkedListFetching,
		refetch: refetchLinkedListDetails
	} = useGetLinkedDevicesListQuery(
		{ gatewayId: getDeviceID() }
	);

	// Mutations for adding device ports
	const [addPortDevice] = useAddPortDeviceMutation();
	// Mutation removing a port to the gateway
	const [removePortDevice] = useRemovePortDeviceMutation();
	// Mutation for approve or block device
	const [approveOrBlock] = useApproveOrBlockDeviceMutation();

	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showAddDevicePortModal, setShowAddDevicePortModal] = useState(false);
	const [connectionData, setConnectionData] = useState<any>({
		isOpen: false,
		data: [],
		deviceName: ""
	});
	const [modalData, setModalData] = useState<any>({
		title: "",
		content: "",
		footerLeftButtonText: "",
		footerRightButtonText: "",
		confirmButtonVariant: "",
		confirm: () => { }

	});
	//linked list header and filter
	const [filterHeaders, setFilterHeaders] =
		useState<LinkedDeviceListHeaderProps[]>(linkedDevicesConnected);
	const [filteredData, setFilteredData] = useState<LinkedDeviceListProps[]>([]);
	const [pageChange, setPageChange] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchValue, setSearchValue] = useState<string>("");
	const [isDevicePortUpdated, setIsDevicePortUpdated] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [toastType, setToastType] = useState<
		"error" | "success" | "warning" | "progress"
	>("success");
	const [toastContent, setToastContent] = useState("");
	const [deviceCloudId, setDeviceCloudId] = useState<string>("");

	// Initialize the table headers and filter headers
	useEffect(() => {
		if (getLinkedListDetails?.data) {
			setFilteredData(getLinkedListDetails.data);
		}
	}, [getLinkedListDetails]);

	// Function to open the modal for adding a device port
	const openDevicePortHandler = (id: string) => {
		setShowAddDevicePortModal(true);
		setDeviceCloudId(id);
	};

	// Function to open the modal for removing a device port
	const openRemovePortHandler = (item: LinkedDeviceListProps) => {
		onModalOpenHandler("Remove device Port", item)
		setDeviceCloudId(item.cloud_id);
	};

	// Function to get action menu items for each device
	// This function returns an array of action items for the device
	const getActionMenuItems = useCallback((item: LinkedDeviceListProps) => {
		return [
			{
				id: `${item.name}-add`,
				label: t(LOCALE_CONST.addDevicePort),
				onClick: () => openDevicePortHandler(item.id)
			},
			{
				id: `${item.name}-remove`,
				label: t(LOCALE_CONST.removeDevicePort),
				onClick: () => openRemovePortHandler(item)
			}
		];
	}, []);

	//toast message timer to hide the toast after 3 seconds
	useEffect(() => {
		if (showToast) {
			const timer = setTimeout(() => {
				setShowToast(false);
			}, 3000);

			return () => clearTimeout(timer);
		}
	}, [showToast]);

	//toast message success handlers
	const showSuccessToast = (message: any) => {
		setToastType("success");
		setToastContent(message);
		setShowToast(true);
	};

	//toast message error handler
	const showErrorToast = (message: string) => {
		setToastType("error");
		setToastContent(message);
		setShowToast(true);
	};

	const extractErrorMessage = (error: string): string => {
		const match = error?.match(/\[(.*?)\]/);
		return match ? match[1] : error;
	};

	//device port add api handler
	const addDevicePortApiHandler = async ({ localPort, portUsage }: { localPort: string; portUsage: string }) => {
		const payload = {
			port: localPort,
			port_usage: portUsage
		};
		console.log("Payload for adding device port:", payload);
		setShowAddDevicePortModal(false);
		setIsDevicePortUpdated(true);

		try {
			const response = await addPortDevice({ deviceId: deviceCloudId, gatewayId: getDeviceID(), payload }).unwrap();
			if (response) {
				showSuccessToast(t(LOCALE_CONST.success_ap_device_200, { deviceCloudId: deviceCloudId }));
				refetchLinkedListDetails();
				//setFilteredData(getLinkedListDetails?.data || []);
				setIsDevicePortUpdated(false);
				setDeviceCloudId("");
				setPageChange(1);
				setSearchValue("");
			}
		} catch (error) {
			const errorMessage = handleApiError(
				error,
				convertFn,
				t(LOCALE_CONST.unknownError),
				{
					400: t(LOCALE_CONST.badRequest_ap_400),
					401: t(LOCALE_CONST.unauthorized_ap_401),
					403: t(LOCALE_CONST.forbidden_ap_403),
					404: t(LOCALE_CONST.notFound_ap_404),
					409: t(LOCALE_CONST.conflict_ap_409),
					500: t(LOCALE_CONST.internalServerError_ap_500),
				}
			);
			showErrorToast(errorMessage);
			setIsDevicePortUpdated(false);
			setDeviceCloudId("");
			setSearchValue("");
		}
	};

	//device port remove api handler
	const removePortDeviceApiHandler = async (item: LinkedDeviceListProps) => {
		setShowConfirmationModal(false);
		setIsDevicePortUpdated(true);
		try {
			const response = await removePortDevice({ deviceId: item.cloud_id, gatewayId: getDeviceID() }).unwrap();
			if (response) {
				showSuccessToast(t(LOCALE_CONST.success_rp_200, { gatewayId: item.cloud_id }));
				refetchLinkedListDetails();
				//setFilteredData(getLinkedListDetails?.data || []);
				setIsDevicePortUpdated(false);
				setDeviceCloudId("");
				setPageChange(1);
				setSearchValue("");
			}
		} catch (error) {
			const errorMessage = handleApiError(
				error,
				convertFn,
				t(LOCALE_CONST.unknownError),
				{
					400: t(LOCALE_CONST.badRequest_rp_400),
					401: t(LOCALE_CONST.unauthorized_rp_401),
					403: t(LOCALE_CONST.forbidden_rp_403),
					404: t(LOCALE_CONST.notFound_rp_404),
					500: t(LOCALE_CONST.internalServerError_rp_500),
				}
			);
			showErrorToast(errorMessage);
			setIsDevicePortUpdated(false);
			setDeviceCloudId("");
			setSearchValue("");
		}
	};

	const approveOrBlockDeviceApiHandler = async (item: LinkedDeviceListProps) => {
		setShowConfirmationModal(false);
		setIsDevicePortUpdated(true);
		const payload = {
			trust_device: item.auth_status === "AUTHORIZED" ? false : true,
			port: item.port
		};
		try {
			const response = await approveOrBlock({ deviceId: item.id, gatewayId: getDeviceID(), payload }).unwrap();
			if (response) {
				if (item.auth_status === "AUTHORIZED") {
					showSuccessToast(t(LOCALE_CONST.success_block_200));
				} else {
					showSuccessToast(t(LOCALE_CONST.success_approve_200));
				}
				refetchLinkedListDetails();
				//setFilteredData(getLinkedListDetails?.data || []);
				setIsDevicePortUpdated(false);
				setDeviceCloudId("");
				setPageChange(1);
				setSearchValue("");
			}
		} catch (error) {
			const errorMessage = handleApiError(
				error,
				convertFn,
				t(LOCALE_CONST.unknownError),
				{
					400: t(LOCALE_CONST.badRequest_app_block_400),
					401: t(LOCALE_CONST.unauthorized_app_block_401),
					403: t(LOCALE_CONST.forbidden_app_block_403),
					404: t(LOCALE_CONST.notFound_app_block_404),
					409: t(LOCALE_CONST.conflict_app_block_409),
					500: t(LOCALE_CONST.internalServerError_app_block_500),
				}
			);
			showErrorToast(errorMessage);
			setIsDevicePortUpdated(false);
			setDeviceCloudId("");
			setSearchValue("");
		}
	};

	const formatContentLinkedListData = (content: LinkedDeviceListProps[]) => {
		const getColorClsName = (status: string = "") => {
			if (status === "disconnected") {
				return "gray";
			} else {
				return "green";
			}
		};

		const formatCellContent = (value: any) => {
			if (Array.isArray(value)) {
				// Handle arrays (e.g., connections)
				return value.map((connection, index) => (
					<div key={index}>
						{connection.user_name} ({connection.connected_at})
					</div>
				));
			} else if (typeof value === "object" && value !== null) {
				// Handle objects
				return JSON.stringify(value);
			}
			return value || "-";
		};
		const displayStatus = (status: string) => {
			if (status === "disconnected") {
				return "Disconnected";
			} else if (status === "connected") {
				return "Connected";
			} else {
				return "-";
			}
		};

		const statusClsName = (status: string) => {
			let clsName = "";

			if (status === GatewayStatus.Error) {
				clsName = "flex items-center";
			}

			return clsName;
		};

		const actionsText = (status: string) => {
			if (status == "AUTHORIZED") {
				return t(LOCALE_CONST.revoke);
			}
			else {
				return t(LOCALE_CONST.authorize);
			}
		};

		const authorisationStatus = (status: string) => {
			if (status === 'AUTHORIZED') {
				return 'green'
			}
			else {
				return 'orange'
			}
		};

		const displayAuthorisationStatus = (status: string) => {
			if (status === 'AUTHORIZED') {
				return t(LOCALE_CONST.authorised);
			}
			else {
				return t(LOCALE_CONST.unauthorized);
			}
		};


		return content.map((item: LinkedDeviceListProps, index: number) => {
			return {
				id: (index + 1).toString(),
				cell: filterHeaders?.map((header) => {
					let cellContent = <span>{formatCellContent(item[header.id])}</span>;

					if (header.id === "health_status") {
						cellContent = (
							<span className={statusClsName(item.health_status)}>
								{item.health_status && (
									<StatusBadge className={`mr-1 text-[0.5rem]`}
										color={getColorClsName(item.health_status)}
									/>
								)}
								{displayStatus(item.health_status)}
							</span>
						);
					}
					else if (header.id === "port_usage") {
						cellContent = (
							<TextOverflow
								resizeDebounceDelay={300}
								tooltipPosition="top"
								className="port-usage-text"
							>
								{item.port_usage || "-"}
							</TextOverflow>

						);
					}
					else if (header.id === 'auth_status') {
						cellContent = (
							<span className={statusClsName(item.auth_status)}>
								{item.health_status && (
									<StatusBadge className={`mr-1 text-[0.5rem]`}
										color={authorisationStatus(item.auth_status)}
									/>
								)}
								{displayAuthorisationStatus(item.auth_status)}
							</span>
						);
					}
					else if (header.id === "connections") {
						cellContent = (
							<div className="connections-buttons-container">
								{item.connections.length > 0 ?
									<Button
										aria-label="Settings"
										data-testid="edit-device"
										className="connections-button-actions"
										variant="quiet-secondary"
										onClick={() => {
											setConnectionData({
												isOpen: true,
												data: item.connections,
												deviceName: item.name
											});
										}}
									> {item.connections.length}</Button>
									: "-"}
							</div>
						);
					}
					else if (header.id === "actions") {
						cellContent = (
							<div className="linkeddevices-action-button-container">
								<div className="linkeddevices-action-button">
									<Button
										aria-label="Settings"
										data-testid="edit-device"
										className="action-remote-connect-linked-devices"
										variant="quiet-secondary"
										onClick={() => onModalOpenHandler(item.auth_status, item)}
									>
										{actionsText(item.auth_status)}
									</Button>
								</div>
								<div className="action-web-remote-url-container">
									{/* <Button
										aria-label="Settings"
										data-testid="connect-remote-device"
										className="action-web-remote-url-button"
										variant="quiet-secondary"
										onClick={() => { window.open("https://www.honeywell.com", "_blank", "noopener noreferrer"); }}
										icon={<OpenInNewIcon />}
									/> */}
									<ContextualMenuComponent
										menuItems={getActionMenuItems(item) || []}
									/>
								</div>
							</div>
						);
					}

					return { content: cellContent };
				})
			};
		});
	};

	const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();

	// Function to get paginated data based on current page and page size
	const getPaginatedData = () => {
		const startIndex = (pageChange - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredData.slice(startIndex, endIndex);
	};

	// Handlers for pagination and sorting
	const pageChangeHandler = (page: number) => {
		console.log("Page changed", page);
		setPageChange(page);
	};

	// Handler for page size change
	const pageSizeChangeHandler = (newPageSize: number) => {
		console.log("Page size changed", newPageSize);
		setPageSize(newPageSize);
		setPageChange(1); // Reset to the first page
	};

	// Handler for sorting
	const sortHandler = (dir: string, id: string) => {
		console.log("Sort changed", dir, id);
	};

	// Search handler to filter data based on search input
	const searchOnChangeHandler = (searchValue: string) => {
		const searchValueLower = normalizeSpaces(searchValue);
		setSearchValue(searchValueLower);

		const filtered = getLinkedListDetails?.data?.filter((data: LinkedDeviceListProps) =>
			Object.values(data || {}).some((value) =>
				value != null && value.toString().toLowerCase().startsWith(searchValueLower)
			)
		);

		// If no matches, set filteredData to an empty array
		setFilteredData(filtered?.length > 0 ? filtered : []);
		setPageChange(1); // Reset to the first page
		console.log("Filtered data:", filtered);
	};

	// Handler for opening the confirmation modal
	const onModalOpenHandler = (item: any, data: any) => {
		setShowConfirmationModal(true);
		if (item === "AUTHORIZED") {
			setModalData({
				title: t(LOCALE_CONST.revokeAuthorisation),
				content: t(LOCALE_CONST.revokeAuthorisationConfirmation),
				footerLeftButtonText: t(LOCALE_CONST.cancel),
				footerRightButtonText: t(LOCALE_CONST.revoke),
				confirmButtonVariant: "destructive-primary",
				confirm: () => approveOrBlockDeviceApiHandler(data)
			});
		} else if (item === "UNAUTHORIZED" || item === "NOT_AUTHORIZED") {
			setModalData({
				title: t(LOCALE_CONST.confirmAuthorisation),
				content: t(LOCALE_CONST.portAuthConfirmation),
				footerLeftButtonText: t(LOCALE_CONST.cancel),
				footerRightButtonText: t(LOCALE_CONST.authorise),
				confirmButtonVariant: "primary",
				confirm: () => approveOrBlockDeviceApiHandler(data)
			});
		} else if (item === "Remove device Port") {
			setModalData({
				title: t(LOCALE_CONST.removePort),
				content: t(LOCALE_CONST.removalWishConfirm, { portName: data.name }),
				footerLeftButtonText: t(LOCALE_CONST.cancel),
				footerRightButtonText: t(LOCALE_CONST.confirm),
				confirmButtonVariant: "destructive-primary",
				confirm: () => removePortDeviceApiHandler(data)
			});
		}
	}

	return (
		<div className="linked-devices-remote-container" >
			<CustomizedTable title={t(LOCALE_CONST.linkedDevices)}
				filterIconClick={() => { }}
				searchOnChange={searchOnChangeHandler}
				searchValue={searchValue}
				onSearchClear={() => {
					setSearchValue("");
					setFilteredData(getLinkedListDetails?.data || []);
					setPageChange(1);
				}}
				onExportAction={() => { }} tableData={filteredData} tableHeaderData={filterHeaders}
				tableContentData={formatContentLinkedListData(getPaginatedData())}
				initialPage={
					pageChange
				}
				onPageChange={
					pageChangeHandler
				}
				onPageSizeChange={pageSizeChangeHandler}
				onUpdateSort={sortHandler}
				maxPages={
					filteredData.length > 0 ? Math.ceil(filteredData.length / pageSize) : 0
				}
				pageSize={pageSize}
				totalItems={filteredData.length}
				totalPages={
					filteredData.length > 0 ? Math.ceil(filteredData.length / pageSize) : 0
				}
				isPaginationEnabled={true}
				isLoading={isLinkedListLoading || isDevicePortUpdated || isLinkedListFetching}
				isError={isLinkedListError}
				errorText={
					linkedListError && 'status' in linkedListError
						? gatewayErrorStatusMessages(
							convertFn,
							t(LOCALE_CONST.unknownError),
							linkedListError.status,
							{
								400: t(LOCALE_CONST.badRequest_ld_400),
								401: t(LOCALE_CONST.unauthorized_ld_401),
								403: t(LOCALE_CONST.forbidden_ld_403),
								404: t(LOCALE_CONST.notFound_ld_404),
								500: t(LOCALE_CONST.internalServerError_ld_500),

							}
						) // Display only the status code
						: "An error occurred"
				}
			/>
			{/* Confirmation Modal */}
			<ConnectConfirmModal
				isOpen={showConfirmationModal}
				onClose={() => setShowConfirmationModal(false)}
				onConfirm={modalData.confirm}
				title={modalData.title}
				content={modalData.content}
				footerLeftButtonText={modalData.footerLeftButtonText}
				footerRightButtonText={modalData.footerRightButtonText}
				confirmButtonVariant={modalData.confirmButtonVariant}
			/>
			{/* Add Device Port Modal */}
			<AddDevicePortModal
				isOpen={showAddDevicePortModal}
				onClose={() => setShowAddDevicePortModal(false)}
				onConfirm={addDevicePortApiHandler}
				title={t(LOCALE_CONST.addDevicePort)}
				content={t(LOCALE_CONST.definePortForConnecion)}
				footerLeftButtonText={t(LOCALE_CONST.cancel)}
				footerRightButtonText={t(LOCALE_CONST.add)}
				confirmButtonVariant="primary"
			/>
			{/* Connected User Modal */}
			<ConnectedUsersModal
				isOpen={connectionData.isOpen}
				onClose={() => setConnectionData({
					isOpen: false,
					data: [],
					deviceName: ""
				})}
				onConfirm={() => setConnectionData({
					isOpen: false,
					data: [],
					deviceName: ""
				})}
				title="Connected Users"
				data={connectionData.data}
				deviceName={connectionData.deviceName}
			/>
			{showToast && (
				<div className="toast-container">
					<Toast
						id="linked-devices-toast"
						showClose={true}
						showDismiss={false}
						title={toastContent}
						type={toastType}
						lifetime={3000}
						subtitle={""}
						content={""}
					/>
				</div>
			)}
		</div>
	);
};

export default ConnectLinkedDevices;